<template>
  <main class="container py-4" role="main">
    <h4 class="mb-3">{{ $t("updateCustomerInfo") }}</h4>

    <div class="py-5 text-center" v-if="isLoading">
      <b-spinner />
    </div>

    <b-form v-if="originalCustomer" class="form-container" @submit.prevent="submitForm">
        <b-form-group>
          <template v-slot:label>{{ $t("customerName") }} <span class="text-danger">*</span></template>
          <b-form-input
            type="text"
            v-model="name"
            autocomplete="name"
            required
            minlength="2"
            maxlength="100"
            :pattern=fullNamePattern
            @invalid="$event.target.setCustomValidity($t('FormatInvalidName'))"
            @input.native="$event.target.setCustomValidity('')"
          />
        </b-form-group>
      <b-form-row>
        <b-form-group class="col-lg-6">
          <template v-slot:label>Email <span class="text-danger">*</span></template>
          <b-form-input v-model.trim="email" type="email" maxlength="100" required autocomplete="email" />
        </b-form-group>
        <b-form-group class="col-lg-6">
          <template v-slot:label>{{ $t("phoneNumber") }} <span class="text-danger">*</span></template>
          <b-form-input type="tel" v-model="phoneNumber" required
                        :pattern="phonePattern" minlength="2" maxlength="50"
                        @invalid="$event.target.setCustomValidity($t('invalidPhoneFormat'))"
                        @input.native="$event.target.setCustomValidity('')" />
        </b-form-group>
      </b-form-row>
      <b-form-group v-if="isUserAdmin()|| isUserSuperAdmin()">
          <template v-slot:label>{{ $t("saleArea2") }} <span class="text-danger">*</span></template>
          <b-form class="d-flex align-items-center" required>
            <b-form-select :options="areaFilterOptions" v-model="areaFilterSelectedOption"/>
          </b-form>
      </b-form-group>
      <b-form-group>
        <template v-slot:label>{{ $t("address") }} <span class="text-danger">*</span></template>
        <b-form-textarea v-model="address" required maxlength="200" />
      </b-form-group>

      <b-form-group style="display: none;">
        <template v-slot:label>{{ $t("storageCapacity") }} <span class="text-info">(1 GB = 1000 MB)</span> <span class="text-danger">*</span></template>
        <b-input-group>
          <b-form-input type="number" min="500" max="1000000" required v-model="recordingStorage"/>
          <b-input-group-append>
            <b-input-group-text>MB</b-input-group-text>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-form-group class="d-flex mt-4 mb-4 align-items-end"  v-if="isUserAdmin() || isManager()">
        <b-input-group>
          <b-form-checkbox v-model="activeGMToken">
            {{ $t("gmTokenConfig") }}
          </b-form-checkbox>
        </b-input-group>
      </b-form-group>
      <b-form-group v-if="(isUserAdmin() || isManager()) && customerPackageId">
        <div v-if="features" class="d-flex mb-3 align-items-end" style="justify-content: space-between;">
          <div style="width: 60%; font-size: 18px; color: #004f9e; font-weight: 400;"><b>{{ $t("featureConfiguration") }} ({{features.length}})</b></div>
          <div style="width: 20%; font-size: 15px; color: #004f9e !important; font-weight: 500;" class="text-muted small text-center">{{ $t("useFeature") }}</div>
          <div style="width: 20%; font-size: 15px; color: #004f9e !important; font-weight: 500;" class="text-muted small text-center">{{ $t("alwaysEnabledByDefault") }}</div>
        </div>
        <div>
          <div
            v-for="(feature, index) in features"
            :key="feature.key"
            class="d-flex align-items-center mb-3"
            style="justify-content: space-between;"
          >
            <div style="width: 60%">{{ index + 1 }}. {{ feature.featureName }}</div>
            <div style="width: 20%" class="text-center">
              <b-form-checkbox 
                v-model="feature.isActive" 
                :value="1" 
                :unchecked-value="0" 
              />
            </div>
            <div style="width: 20%" class="text-center">
              <b-form-checkbox 
                v-model="feature.isDefault" 
                :value="1" 
                :unchecked-value="0"
                :disabled="!feature.isActive" 
              />
            </div>
          </div>
        </div>
      </b-form-group>

      <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>

      <div class="text-center">
        <b-button type="submit" variant="primary" :disabled="isSubmitting" class="mt-4">{{ $t("update") }}</b-button>
      </div>
    </b-form>
  </main>
</template>

<style lang="scss" scoped>
  .form-container {
    max-width: 700px;
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import moment from "moment";
  import BaseComponent from "@/components/BaseComponent";
  import * as areaService from "@/service/areaService";
  import * as customerService from "@/service/customerService";
  import * as validationUtils from "@/service/validationUtils";

  import provinces from "@/provinces.json";
  import * as util from "@/service/util";
  import { sanitizeObject } from "@/service/util";
  import { Customer } from '@/model/utilModels';
  import { Status } from '../enums/EnableStatus';
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";
  import * as featureConfigService from "@/service/featureConfigService";

  @Component
  export default class EditCustomer extends BaseComponent {

    id: number = null;
    isLoading = false;
    originalCustomer = null;

    datePickerFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

    code = "1234";

    name = "";
    email = "";
    phoneNumber = "";
    address = "";
    recordingStorage = 1000;

    areaFilterSelectedOption: number = null;
    areaId:number = null;
    areaFilterOptions = [];
    activeFaceId = false;
    activeVoiceText = false;
    activeGMToken = false;
    features = [];
    isCreatingUser = false;

    formErrorMsg = "";
    isSubmitting = false;

    provinceOptions = provinces;
    province = "";

    /// ID gói cước của Khách hàng
    customerPackageId = null;

    mounted() {
      this.id = +this.$route.params.id;
      this.fetchCustomer();
      this.fetchAreas();      
    }

    provinceSelectSearch(option, label, searchText) {
      searchText = util.sanitizeSearchText(searchText.toLowerCase());
      return util.sanitizeSearchText(label).toLowerCase().includes(searchText);
    }

    async fetchAreas() {
      try {
        const areas = await areaService.getAll();
        this.areaFilterOptions = areas.map(it => ({ value: it.id, text: it.name }));        
      } catch (e) {
        this.toastError(e);
      }
    }

    async fetchCustomer() {
      try {
        this.isLoading = true;
        const customer = await customerService.getDetails(this.id);
        const subcription = await subscriptionPlanService.getCustomerSubscription(this.id);
        // Gọi song song 3 API đầu tiên để tăng hiệu năng
        // let [subscriptions, subscriptionHistory, storageHistory] = await Promise.all([
        //   subscriptionPlanService.getCustomerSubscription(this.id),
        //   subscriptionPlanService.getCustomerSubscriptionHistoryList(this.id),
        //   subscriptionPlanService.getCustomerStorageSubscriptionHistoryList(this.id),
        // ]);
          // let subcriptionId;
          subcription.forEach(ele => {            
            if (ele.isActive){
              this.customerPackageId = ele.subscriptionPlanId;
            }
          });
          let featureSubcription = null;
          let featureConfigCustomer = null;
          
          // Nếu có subscriptionId → gọi song song
          if (this.customerPackageId) {            
            [featureSubcription, featureConfigCustomer] = await Promise.all([
              featureConfigService.getFeaturesBySubscriptionPlanId(this.customerPackageId),
              customerService.getFeatureConfigCustomer(this.id),
            ]);
          } 
          // Nếu không có → chỉ gọi API customer
          // else {
          //   featureConfigCustomer = await customerService.getFeatureConfigCustomer(this.id);
          // }
        
        this.originalCustomer = customer;
        this.code = customer.customerCode;
        this.name = customer.name;
        this.email = customer.email;
        this.phoneNumber = customer.phoneNumber;
        this.province = customer.province;    
        this.areaFilterSelectedOption = customer.areaId;       
        this.address = customer.address;
        this.recordingStorage = customer.recordingStorage;

        this.activeFaceId = customer.activeFaceId == Status.ENABLE ? true : false;
        this.activeVoiceText = customer.activeVoiceText == Status.ENABLE ? true : false;
        this.activeGMToken = customer.activeGMToken == Status.ENABLE ? true : false; 

        /// get feature config of a customer
        const featuresCustomer = featureConfigCustomer?.data?.features ?? [];
        const featuresSystem = featureSubcription?.data?.features ?? [];

        ///
        if (Array.isArray(featuresSystem)) {          
          featuresSystem.forEach(elementSubcript => {
            featuresCustomer.forEach(elementCustomer => {
              if (elementSubcript.featureId == elementCustomer.featureId){
                elementSubcript.isActive = elementCustomer.isActive,
                elementSubcript.isDefault = elementCustomer.isDefault;
              }
            });
          });
        }

        ///
        this.features = featuresSystem;        
      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoading = false;
      }
    }

    async submitForm() {
      this.formErrorMsg = "";
      this.name = this.name.trim();
      if (!validationUtils.isEmail(this.email)) {
        this.formErrorMsg = this.$t("invalidEmail").toString();
        return;
      }

      const data = sanitizeObject<Customer>({
        id: this.id,
        customerCode: this.code,
        areaId: this.areaFilterSelectedOption,
        name: this.name,
        email: this.email.toLowerCase(),
        phoneNumber: this.phoneNumber,
        province: this.province,
        address: this.address,
        recordingStorage: +this.recordingStorage,

        activeFaceId: this.activeFaceId == true ? Status.ENABLE : Status.DISABLE,
        activeVoiceText: this.activeVoiceText == true ? Status.ENABLE : Status.DISABLE,
        activeGMToken: this.activeGMToken == true ? Status.ENABLE : Status.DISABLE,
      });

      /**
       * Quy đổi lại các giá trị isActive, isDefault của feature sang dạng số để lưu vào DB
       */
      const dataFeatureConfig = {
        customerId: this.id,
        features: this.features
      };
      try {       
        this.isSubmitting = true;
        await customerService.updateCustomer(data);

        /// Nếu Khách hàng có thông tin gói cước => cập nhật các chức năng của Khách hàng
        if(this.customerPackageId) {
          await customerService.updateFeatureCustomer(dataFeatureConfig);
        }
        
        await this.$router.push("/customers/details/" + this.id);
        this.toastSuccessDelay(this.$t("updateCustomerInfoSuccessful").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
